import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  getMessengerReplyModeLabelKey,
  type MessengerComposerContext,
  type MessengerReplyMode,
} from '../utils/messengerComposerUtils';
import { truncateMessageSnippet } from '../utils/messageUtils';
import styles from './ConversationDetails.module.css';

export interface MessengerComposerReplyTargetProps {
  context: MessengerComposerContext;
  replyMode: MessengerReplyMode;
  selectedCommentMessageId?: string | null;
  onReplyModeChange: (mode: MessengerReplyMode) => void;
  onCommentTargetChange: (messageId: string) => void;
  onClear: () => void;
  onJumpToTarget: () => void;
}

export const MessengerComposerReplyTarget: React.FC<MessengerComposerReplyTargetProps> = ({
  context,
  replyMode,
  selectedCommentMessageId,
  onReplyModeChange,
  onCommentTargetChange,
  onClear,
  onJumpToTarget,
}) => {
  const { t } = useTranslation('conversations');
  const activeTargetId = selectedCommentMessageId ?? null;
  const selectedTarget = context.unrepliedCommentOptions.find(option => option.messageId === activeTargetId);
  const showTargetSelect = context.unrepliedCommentOptions.length > 1;
  const commentModes = context.availableReplyModes.filter(
    mode => mode === 'private_comment' || mode === 'public_comment',
  );
  const showModeToggle = commentModes.length > 1;
  const shellClass = replyMode === 'public_comment'
    ? styles.composerInstagramReplyTargetPublic
    : styles.composerInstagramReplyTargetPrivate;

  if (!selectedTarget) {
    return null;
  }

  const formatOptionLabel = (option: typeof selectedTarget): string => {
    const postLabel = t(option.postLabelKey);
    const snippet = truncateMessageSnippet(
      option.commentText || option.postCaption || t('messengerContentPreviewNoCaption'),
      72,
    );
    return `${postLabel} · ${snippet}`;
  };

  return (
    <div className={`${styles.composerInstagramReplyTarget} ${shellClass}`}>
      <div className={styles.composerInstagramReplyTargetRow}>
        <button
          type="button"
          className={styles.composerInstagramReplyTargetJump}
          onClick={onJumpToTarget}
        >
          <div className={styles.composerInstagramReplyTargetBody}>
            <span className={styles.composerInstagramReplyTargetLabel}>
              {t('messengerComposerReplyTargetLabel', { mode: t(getMessengerReplyModeLabelKey(replyMode)) })}
            </span>
            <span className={styles.composerInstagramReplyTargetMeta}>
              {t(selectedTarget.postLabelKey)}
            </span>
            <span className={styles.composerInstagramReplyTargetText}>
              {truncateMessageSnippet(
                selectedTarget.commentText || selectedTarget.postCaption || t('messengerContentPreviewNoCaption'),
                140,
              )}
            </span>
          </div>
        </button>
        <button
          type="button"
          className={styles.composerQuotedMessageClear}
          onClick={onClear}
          title={t('messengerComposerReplyTargetClear')}
        >
          {t('close', { ns: 'common' })}
        </button>
      </div>

      {showTargetSelect ? (
        <label className={styles.composerInstagramReplyTargetSelectWrap}>
          <span className={styles.composerInstagramReplyTargetSelectLabel}>{t('messengerReplyTargetSelectLabel')}</span>
          <select
            className={styles.composerInstagramReplyTargetSelect}
            value={selectedTarget.messageId}
            onChange={event => onCommentTargetChange(event.target.value)}
            aria-label={t('messengerReplyTargetSelectLabel')}
          >
            {context.unrepliedCommentOptions.map(option => (
              <option key={option.messageId} value={option.messageId}>
                {formatOptionLabel(option)}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {showModeToggle ? (
        <div className={styles.composerInstagramReplyModeToggle} role="group" aria-label={t('messengerReplyModeLabel')}>
          {commentModes.map(mode => (
            <button
              key={mode}
              type="button"
              className={`${styles.composerInstagramReplyModeButton} ${replyMode === mode ? styles.composerInstagramReplyModeButtonActive : ''}`}
              aria-pressed={replyMode === mode}
              onClick={() => onReplyModeChange(mode)}
            >
              {t(getMessengerReplyModeLabelKey(mode))}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};
